import React from 'react';

// Shared building blocks for every comparison card template.
//
// EXPORT SAFETY: no object-fit, no object-position, no clip-path, no filter.
// html2canvas@1.4.1 implements none of them and fails silently.
// See lib/photoRender.js.

export const CARD_FONT = "'Barlow Condensed', sans-serif";
export const ORANGE = '#FF5C00';
export const HAIRLINE = 'rgba(255,255,255,0.12)';

// Every size in a template is written for a 1920px-tall story card and then
// multiplied by this, so one set of numbers serves all four ratios.
export const scaleFor = (H) => H / 1920;

export function CardShell({ W, H, padding = 0, background = '#0d0d0f', children }) {
  return (
    <div
      style={{
        position: 'relative',
        width: W,
        height: H,
        background,
        color: '#ffffff',
        fontFamily: CARD_FONT,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding,
        boxSizing: 'border-box',
      }}
    >
      {children}
    </div>
  );
}

export function BrandFooter({ handleLabel, right = null, s = 1, bordered = true }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: bordered ? `${Math.max(1, 2 * s)}px solid ${HAIRLINE}` : 'none',
        paddingTop: bordered ? 18 * s : 0,
        fontSize: 26 * s,
        letterSpacing: 2 * s,
        textTransform: 'uppercase',
        opacity: 0.55,
      }}
    >
      <span>
        trainedby<span style={{ color: ORANGE, opacity: 1 }}>RS</span>.com
      </span>
      <span>{right || handleLabel || ''}</span>
    </div>
  );
}

export function DeltaHero({ hero, s = 1, align = 'left' }) {
  // lineHeight: 0.92 belongs on the value line only — Barlow Condensed needs
  // ~1.2em, and having it on the wrapper squeezed the sub line's box down
  // with it, so the 128*s value overflowed upward and struck through the
  // 26*s sub label above it (visible on Editorial 9:16 with numbers hidden).
  return (
    <div style={{ textAlign: align }}>
      {hero.sub && (
        <div style={{ fontSize: 26 * s, letterSpacing: 4 * s, color: ORANGE, fontWeight: 700 }}>
          {hero.sub}
        </div>
      )}
      <div style={{ fontSize: 128 * s, fontWeight: 800, letterSpacing: -2 * s, lineHeight: 0.92 }}>
        {hero.value}
        {hero.unit && <span style={{ fontSize: 52 * s, opacity: 0.6 }}>{hero.unit}</span>}
      </div>
    </div>
  );
}

export function PhotoTag({ children, tone = 'dark', s = 1, style }) {
  return (
    <div
      style={{
        position: 'absolute',
        fontSize: 26 * s,
        letterSpacing: 3 * s,
        textTransform: 'uppercase',
        fontWeight: 600,
        padding: `${6 * s}px ${14 * s}px`,
        borderRadius: 10 * s,
        background: tone === 'accent' ? ORANGE : 'rgba(0,0,0,0.72)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function StatChip({ value, label, s = 1, tone = 'muted' }) {
  return (
    <div
      style={{
        flex: 1,
        textAlign: 'center',
        borderRadius: 14 * s,
        padding: `${12 * s}px 0`,
        background: tone === 'accent' ? 'rgba(255,92,0,0.15)' : 'rgba(255,255,255,0.06)',
      }}
    >
      <div
        style={{
          fontSize: 40 * s,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: tone === 'accent' ? ORANGE : '#ffffff',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 20 * s, opacity: 0.5, letterSpacing: 2 * s }}>{label}</div>
    </div>
  );
}
